import { useRef } from 'react';

import { CFPInputField } from '@bcp-channels-sharedcomponents-reactjs/cf-proxy';
import { useDidMount } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { InputFieldModels } from '@bcp-nextgen-dx-component-factory/design-system';
import { CollapsibleBlock, InvalidDataAlert } from '@components';
import { AppHelper, LoggerHelper } from '@helpers';
import { useAlert, useBrowserInfoCookie, useLoginForm, useModal, useTranslation } from '@hooks';
import { useBlockStepManager } from '@modules';
import { useFetchAccountLogin } from '@services';
import { LoginBlock, useAmaStore, useSaveServiceResponse, useSearchParamsStore, useServicesStore } from '@store';
import { ChallengeTypeEnum } from '@types';

import { CmdBlock } from './cmd';

const resources = {
	title: 'screens.login.form.alias.title',
	tooltip: {
		title: 'screens.login.form.alias.tooltip.title',
		description: 'screens.login.form.alias.tooltip.description',
	},
	input: {
		label: 'screens.login.form.alias.input.label',
	},
	closed: {
		title: 'screens.login.form.alias.closed.title',
		link: {
			text: 'screens.login.form.alias.closed.link.text',
		},
	},
	button: {
		continue: 'screens.login.button.continue',
	},
	aliasWrongContext: {
		1: 'general.aliasWrongContext.individuals.link',
		8: 'general.aliasWrongContext.corporate.link',
	},
	oldSiteUrl: 'general.oldSiteUrl',
};

export interface AliasBlockProps {
	isActive?: boolean;
	onClosedContentLinkClick?: () => void;
	onOpenContentButtonClick?: () => void;
}

export const AliasBlock = ({ isActive, onClosedContentLinkClick, onOpenContentButtonClick }: AliasBlockProps) => {
	const { t } = useTranslation();
	const { goto } = useBlockStepManager();
	const { initBrowserInfoCookie } = useBrowserInfoCookie();
	const { alias, setState, resetState } = useLoginForm();

	const invalidDataAlert = useAlert('invalidData');
	const genericErrorModal = useModal('genericError');

	const clientId = useSearchParamsStore((store) => store.clientId);
	const language = useSearchParamsStore((state) => state.language);
	const { errorMessage } = useServicesStore((state) => state.accountLogin) || {};
	const userAccountAlias = useServicesStore((state) => state.accountUser?.userAlias);
	const saveServiceResponse = useSaveServiceResponse();
	const clearAmaState = useAmaStore((state) => state.clearState);

	const { fetch: fetchAccountLogin } = useFetchAccountLogin();

	const inputRef = useRef<InputFieldModels.InputFieldRef>(null);

	useDidMount(() => {
		// automatically log the user when AccountUser service responds successfully
		if (!AppHelper.isBeRedirect && alias && userAccountAlias) {
			handleOpenContentButtonClick();
		}
	});

	/* store Alias value in loginFormStore on input change */
	const handleInputFieldChange = (value?: string) => {
		/* only allow alpha numeric characters to be inserted in the input */
		const alphaNumericRegex = /^[a-zA-Z0-9 ]+$/;
		if (!value || alphaNumericRegex.test(value)) {
			const newAlias = value?.replaceAll(' ', '').trim() || '';

			LoggerHelper.debug('App', 'AliasBlock > handleInputFieldChange:', { value, newAlias });

			setState({
				form: { alias: newAlias },
			});
		}
	};

	const handleClosedContentLinkClick = () => {
		/* when user clicks the "Enter with new code" link, */

		/* hide invalidData alert */
		invalidDataAlert.hide();
		/* reset form state (persisting "alias" value) */
		resetState({ form: { alias } });
		/* reset accountChallenge response */
		saveServiceResponse({ accountChallenge: {} });
		/* callback prop (used in parent for the mobile expandBlock functionality - login.mobile) */
		onClosedContentLinkClick?.();
		/* reposition blockStepManager in Alias block */
		goto(LoginBlock.Alias);
	};

	const handleOpenContentButtonClick = async () => {
		try {
			invalidDataAlert.hide();
			inputRef.current?.blur();

			clearAmaState();

			/* init BrowserInfo cookie after accountLogin success */
			const cookieCifra = await initBrowserInfoCookie(alias || '');

			const res = await fetchAccountLogin({ userAlias: alias, cookieCifra, language: language?.toUpperCase() });

			const { oldSite, wrongContext, challengeType, userAliasTypeId, needsSafe, needsTaxNumber, errorMessage } =
				res.data || {};

			/* user not allowed to login in newSite, exit to oldSite */
			if (oldSite) {
				window.location.href = t(resources.oldSiteUrl);
				return;
			}

			/* userAliasTypeId not valid for corresponding clientId, exit app */
			if (wrongContext) {
				if (!userAliasTypeId || (userAliasTypeId !== 1 && userAliasTypeId !== 8)) {
					/* FE needs to know the userAliasTypeId to redirect to the correct link */
					LoggerHelper.error(
						'App',
						'AliasBlock > handleOpenContentButtonClick - WrongContext but UserAliasTypeId not defined:',
						{ output: res.data },
						{ overlay: { show: true } }
					);

					genericErrorModal.show();
					return;
				}

				const resourceKey = resources.aliasWrongContext[userAliasTypeId];
				window.location.href = t(resourceKey);

				return;
			}

			if (challengeType === ChallengeTypeEnum.None) {
				if (errorMessage) {
					invalidDataAlert.show();
				} else {
					genericErrorModal.show();
					throw new Error(
						'Can not handle account/login response:' + JSON.stringify({ challengeType }, undefined, 2)
					);
				}

				return;
			}

			/* used for the block animation in the login.mobile screen */
			onOpenContentButtonClick?.();

			navigateNextBlock(challengeType, needsTaxNumber, needsSafe);
		} catch (err) {
			const catchError = AppHelper.convertCatchError(err);

			LoggerHelper.error('App', 'AliasBlock > handleOpenContentButtonClick [catch]:', catchError, {
				overlay: { show: true },
			});
		}
	};

	const navigateNextBlock = (
		challengeType?: ChallengeTypeEnum | null,
		needsTaxNumber?: boolean | null,
		needsSafe?: boolean | null
	) => {
		/* set flow state */
		const flow =
			challengeType === ChallengeTypeEnum.Password
				? needsTaxNumber
					? 'passwordWithTaxNumber'
					: 'password'
				: needsSafe
				? 'multichannelWithSafe'
				: 'multichannel';

		setState({ flow });

		/* navigate */
		const block = challengeType === ChallengeTypeEnum.Password ? LoginBlock.Password : LoginBlock.Multichannel;
		goto(block);
	};

	const openContentChildren = (
		<>
			<CFPInputField
				ref={inputRef}
				name="userAlias"
				label={t(resources.input.label)}
				value={alias}
				onChange={handleInputFieldChange}
				maxLength={20}
				autoFocus
			/>

			<InvalidDataAlert description={errorMessage || ''} />
		</>
	);

	const isCorporateClient = AppHelper.isCorporateClient(t('configs.corporateClientsList'), clientId);
	const isContinueDisabled = !alias || alias.length < 6;

	return (
		<CollapsibleBlock
			isOpen={isActive}
			openContent={{
				label: {
					value: t(resources.title),
					tooltip: {
						title: t(resources.tooltip.title),
						description: t(resources.tooltip.description),
					},
				},
				button: {
					label: t(resources.button.continue),
					disabled: isContinueDisabled,
					onClick: handleOpenContentButtonClick,
				},
				children: openContentChildren,
			}}
			closedContent={{
				label: t(resources.closed.title),
				value: alias,
				link: {
					label: t(resources.closed.link.text),
					onClick: handleClosedContentLinkClick,
				},
			}}
			extraContent={<CmdBlock isVisible={isActive && !isCorporateClient} />}
		/>
	);
};
